import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { allShapes, readPresentation, type ShapeInfo } from "@/office/powerpoint/presentation";
import { nichtPruefbar } from "../schnittstelle";

/** Formen, die jemand selbst auf die Folie gezogen hat. */
function gezeichneteFormen(shapes: ShapeInfo[]): ShapeInfo[] {
  return shapes.filter(
    (form) =>
      form.type === "GeometricShape" ||
      form.type === "Callout" ||
      form.type === "Freeform" ||
      form.type === "Line" ||
      form.type === "SmartArt" ||
      form.type === "Diagram",
  );
}

export const lektionFormen: Lesson = {
  id: "formen",
  title: "Formen, Linien und SmartArt",
  summary: "Zusammenhänge zeigen statt beschreiben – und alles exakt ausrichten.",
  durationMinutes: 10,
  learningGoals: [
    "Formen und Verbindungslinien gezielt einsetzen",
    "SmartArt für Hierarchien und Abläufe verwenden",
    "Objekte über Ausrichten statt mit dem Auge positionieren",
  ],
  steps: [
    {
      id: "formen-einsetzen",
      title: "Wann eine Form mehr sagt als ein Stichpunkt",
      explanation: [
        "Drei Kästen mit Pfeilen dazwischen erklären einen Ablauf in einer Sekunde. " +
          "Derselbe Ablauf als drei Stichpunkte braucht einen Satz Erklärung dazu – " +
          "„und zwar in dieser Reihenfolge“ –, den man leicht überliest.",
        "Formen sind deshalb kein Schmuck, sondern ein Mittel, Beziehungen sichtbar zu " +
          "machen: Reihenfolge, Zugehörigkeit, Gegensatz, Größenverhältnis. Alles, was " +
          "eine räumliche Entsprechung hat, ist als Grafik besser aufgehoben.",
        "Beim Verbinden von Formen lohnt sich ein Detail: Die Linien unter „Einfügen“ → " +
          "„Formen“ → „Linien“ sind zum Teil Verbinder. Zieht man einen Verbinder von " +
          "einem Verbindungspunkt einer Form zu einem anderen, bleibt er daran hängen – " +
          "verschiebst du später eine der Formen, wächst die Linie mit. Eine gewöhnliche " +
          "Linie bleibt liegen, wo sie war.",
      ],
      keyPoints: [
        "Einfügen → Formen; Umschalt beim Ziehen erzwingt Quadrat, Kreis oder gerade Linie.",
        "Verbinder rasten an den Verbindungspunkten einer Form ein und wachsen mit.",
        "Strg + D dupliziert eine Form mit gleichem Aussehen.",
      ],
      task: {
        instruction:
          "Füge auf einer Folie mindestens zwei Formen ein, die einen Zusammenhang " +
          "darstellen – zum Beispiel zwei Kästen mit einem Pfeil dazwischen.",
        hint: "Einfügen → Formen. Mit Strg + D lässt sich der erste Kasten schnell verdoppeln.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form",
              "1.4",
              "Prüfe selbst, ob auf einer Folie mindestens zwei gezeichnete Formen liegen.",
            );
          }

          const proFolie = snapshot.slides.map((folie) => ({
            folie,
            formen: gezeichneteFormen(folie.shapes),
          }));
          const treffer = proFolie.find((eintrag) => eintrag.formen.length >= 2);

          if (treffer) {
            return pass(
              `Auf Folie ${treffer.folie.index + 1} liegen ${treffer.formen.length} Formen – der Zusammenhang ist sichtbar statt beschrieben.`,
            );
          }

          const gesamt = proFolie.reduce((summe, eintrag) => summe + eintrag.formen.length, 0);
          return fail(
            gesamt === 0
              ? "Es ist noch keine Form eingefügt."
              : `Es gibt erst ${gesamt} Form${gesamt === 1 ? "" : "en"}, aber keine Folie mit zweien.`,
            "Einfügen → Formen → Rechteck aufziehen, mit Strg + D duplizieren, dann einen Pfeil dazwischensetzen.",
          );
        },
      },
    },
    {
      id: "ausrichten",
      title: "Ausrichten statt mit dem Auge schieben",
      explanation: [
        "Drei Kästen, die fast auf einer Linie liegen, wirken unruhiger als drei Kästen, " +
          "die deutlich versetzt sind. Das Auge merkt die Abweichung, kann sie aber nicht " +
          "einordnen – und das strengt an.",
        "Deshalb wird nicht mit der Maus ausgerichtet, sondern mit der Funktion dafür: " +
          "Alle Objekte markieren, dann „Start“ → „Anordnen“ → „Ausrichten“. " +
          "„Linksbündig“, „Oben ausrichten“ und Ähnliches setzen sie exakt auf eine " +
          "Linie. „Horizontal verteilen“ sorgt zusätzlich für gleiche Abstände.",
        "Beim Verteilen gibt es einen Stolperstein: Ist unter „Ausrichten“ die Option " +
          "„An Folie ausrichten“ aktiv, bezieht sich alles auf die Folienränder. Mit " +
          "„Ausgewählte Objekte ausrichten“ bezieht es sich auf die Auswahl. Beides ist " +
          "richtig – nur eben in unterschiedlichen Situationen.",
      ],
      keyPoints: [
        "Start → Anordnen → Ausrichten für exakte Linien und gleiche Abstände.",
        "„An Folie ausrichten“ oder „Ausgewählte Objekte ausrichten“ bewusst wählen.",
        "Pfeiltasten verschieben in kleinen Schritten, mit Strg noch feiner.",
      ],
      task: {
        instruction:
          "Richte mindestens zwei Formen auf einer Folie exakt aneinander aus – gleiche " +
          "Oberkante oder gleiche linke Kante.",
        hint: "Beide Formen mit Strg-Klick markieren, dann Start → Anordnen → Ausrichten → „Oben ausrichten“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Position und Größe einer Form",
              "1.4",
              "Kontrolliere selbst über „Start“ → „Anordnen“ → „Ausrichten“, ob die Formen auf einer Linie liegen.",
            );
          }

          // Exakt ausgerichtete Objekte haben identische Koordinaten. Eine halbe
          // Punktbreite Toleranz fängt Rundungen beim Skalieren ab.
          const toleranz = 0.5;
          for (const folie of snapshot.slides) {
            const formen = gezeichneteFormen(folie.shapes);
            if (formen.length < 2) continue;

            for (let i = 0; i < formen.length; i += 1) {
              for (let j = i + 1; j < formen.length; j += 1) {
                const a = formen[i];
                const b = formen[j];
                const gleicheOberkante =
                  a.top !== null && b.top !== null && Math.abs(a.top - b.top) <= toleranz;
                const gleicheLinkeKante =
                  a.left !== null && b.left !== null && Math.abs(a.left - b.left) <= toleranz;
                if (gleicheOberkante || gleicheLinkeKante) {
                  return pass(
                    `Auf Folie ${folie.index + 1} liegen „${a.name ?? "Form"}“ und „${b.name ?? "Form"}“ ` +
                      `auf derselben ${gleicheOberkante ? "Oberkante" : "linken Kante"}.`,
                  );
                }
              }
            }
          }

          const mitFormen = snapshot.slides.filter(
            (folie) => gezeichneteFormen(folie.shapes).length >= 2,
          );
          return fail(
            mitFormen.length === 0
              ? "Es gibt noch keine Folie mit mindestens zwei Formen."
              : `Auf Folie ${mitFormen[0].index + 1} liegen zwar mehrere Formen, aber keine zwei teilen sich eine Kante.`,
            "Beide Formen markieren, dann Start → Anordnen → Ausrichten → „Oben ausrichten“ oder „Linksbündig“.",
          );
        },
      },
    },
    {
      id: "smartart",
      title: "SmartArt für Hierarchien und Abläufe",
      explanation: [
        "SmartArt nimmt eine Textgliederung und macht daraus eine fertige Grafik: " +
          "Organigramm, Prozesskette, Kreislauf, Pyramide, Venn-Diagramm. Der große " +
          "Vorteil ist, dass die Grafik aus Text besteht und Text bleibt – Änderungen " +
          "tippt man im Textbereich, die Grafik ordnet sich neu an.",
        "Der schnellste Weg dorthin führt über eine bestehende Aufzählung: Text " +
          "markieren → Rechtsklick → „In SmartArt-Grafik konvertieren“. Aus einer " +
          "eingerückten Liste wird auf Anhieb ein Organigramm, weil SmartArt die " +
          "Gliederungsebenen als Hierarchie liest. Genau deshalb lohnt es sich, die " +
          "Ebenen sauber über die Tabulatortaste zu setzen.",
        "Zwei Einschränkungen sind wichtig. Erstens verträgt SmartArt keine großen " +
          "Textmengen – sobald die Kästen viel Text enthalten, schrumpft die Schrift und " +
          "die Grafik wird unlesbar. Zweitens ist SmartArt für Screenreader nur " +
          "eingeschränkt zugänglich; ein Alternativtext für die gesamte Grafik ist " +
          "deshalb Pflicht. Dazu mehr im Modul Barrierefreiheit.",
      ],
      keyPoints: [
        "Einfügen → SmartArt, oder Rechtsklick auf eine Aufzählung → In SmartArt konvertieren.",
        "Die Gliederungsebenen der Liste werden zur Hierarchie der Grafik.",
        "SmartArt braucht wenig Text und immer einen Alternativtext.",
      ],
      task: {
        instruction:
          "Wandle eine deiner Aufzählungen in eine SmartArt-Grafik um oder füge eine " +
          "neue ein.",
        hint: "Text markieren → Rechtsklick → „In SmartArt-Grafik konvertieren“.",
        check: async () => {
          const snapshot = await readPresentation();

          if (!snapshot.features.shapeDetails) {
            return nichtPruefbar(
              "Die Art einer Form",
              "1.4",
              "Sieh selbst nach, ob auf einer Folie eine SmartArt-Grafik liegt.",
            );
          }

          const smartArt = allShapes(snapshot).filter(
            (form) =>
              form.type === "SmartArt" ||
              form.type === "Diagram" ||
              form.placeholder?.containedType === "SmartArt",
          );

          if (smartArt.length > 0) {
            return pass(
              `SmartArt-Grafik gefunden – auf Folie ${smartArt[0].slideIndex + 1}. ` +
                "Denk an den Alternativtext; das Modul Barrierefreiheit kommt darauf zurück.",
            );
          }
          return fail(
            "Es ist noch keine SmartArt-Grafik in der Präsentation.",
            "Markiere eine Aufzählung im Inhaltsplatzhalter, Rechtsklick → „In SmartArt-Grafik konvertieren“ und wähle eine Prozess- oder Hierarchiedarstellung.",
          );
        },
      },
    },
  ],
};
